import { grey } from "@mui/material/colors";
import darkScrollbar from "@mui/material/darkScrollbar";
import { createTheme } from "@mui/material/styles";
import { connect } from "react-redux";
import { set_theme } from "../../redux/shared/shared.methods";

export const TuxisTheme = (prefersDarkMode) => {
	const mode = prefersDarkMode ? "dark" : "light";
	/* ------------------------------ LIGHT PALETTE ----------------------------- */

	const PaletteLight = {
		app_bar: { main: "#ffffff60" },
		background: { default: "#ffffff" },
		button_color: { main: "#1985b3" },
		card_header: { main: "#04497512", no_shine_through: "#DEE6EC" },
		card_shine_through_company_light: { main: "#2eabe12b" },
		card_shine_through_error: { main: "#ff000040" },
		card_shine_through_primary: { main: "#0449750D" },
		card_shine_through_secondary: { main: "#F370320D" },
		card_shine_through_success: { main: "rgba(76, 175, 80, 0.1)" },
		card_shine_through_warning: { main: "rgba(255, 153, 0, 0.1)" },
		company_light: { contrastText: "#FFFFFF", dark: "#1985b3", main: "#2EAAE1" },
		config_footer: { border: "1px solid #cccccc;", main: "#f9f9fb50" },
		custom_chip: "#04497512",
		discount_rule: "#0449750D",
		error: { main: "#D32F2F" },
		grey: { contrastText: "#ffffff", disabled: "#bdbdbd", main: "#4d4d4d" },
		hyperlink: "#2EAAE1",
		impersonate: { contrastText: "#ffffff", main: "#4d4d4d" },
		primary: { dark: "#03365c", main: "#044975" },
		proxmox_colors: { contrastText: "#ffffff", dark: "#b15f02", main: "#eb7e02" },
		secondary: { contrastText: "#ffffff", dark: "#d94d0d", main: "#F37032" },
		slider_input: { contrastText: "#044975", main: "#ffffff" },
		succes: { main: "#4CAF50" },
		vps_config_dividers: "#0000000D",
		vps_status_change: "#0449750D",
		warning: { main: "#FF9800" },
	};

	/* ------------------------------ DARK PALETTE ------------------------------ */

	const PaletteDark = {
		app_bar: { main: "#12182160" },
		background: { default: "#121821" },
		button_color: { main: "#16749c" },
		card_header: { main: "#04497530", no_shine_through: "#032f4a" },
		card_shine_through_company_light: { main: "#1985b339" },
		card_shine_through_error: { main: "#ff000040" },
		card_shine_through_primary: { contrastText: "#E1E1E1", main: "#04497528" },
		card_shine_through_secondary: { main: "#F3703228" },
		card_shine_through_success: { main: "rgba(35, 134, 54, 0.1)" },
		card_shine_through_warning: { main: "rgba(255, 153, 0, 0.1)" },
		company_light: { contrastText: "#FFFFFF", dark: "#136386", main: "#1985b3" },
		config_footer: { border: "1px solid #021f31;", main: "#04497550" },
		custom_chip: "#04497550",
		discount_rule: "#04497520",
		error: { main: "#ff3333" },
		grey: { contrastText: "#FFFFFF", main: "#9096a2" },
		hyperlink: "#2eaae1",
		impersonate: { contrastText: "#ffffff", main: "#333333" },
		primary: {
			contrastText: "#000000",
			dark: "#01253e",
			main: "#2eaae1",
		},
		proxmox_colors: { contrastText: "#ffffff", dark: "#b15f02", main: "#eb7e02" },
		secondary: { main: "#d94d0d" },
		slider_input: { contrastText: "#ffffff", main: "#04497550" },
		success: { main: "#238636" },
		vps_config_dividers: "#ffffff15",
		vps_status_change: "#021f31",
		warning: { main: "#FDB813" },
	};
	const palette = prefersDarkMode ? PaletteDark : PaletteLight;

	return createTheme({
		breakpoints: {
			values: {
				lg: 1200,
				md: 900,
				sm: 600,
				xl: 1536,
				xs: 0,
				xxl: 2000,
			},
		},
		components: {
			MuiAccordion: {
				styleOverrides: {
					root: {
						"&:first-of-type": {
							borderTopLeftRadius: 16,
							borderTopRightRadius: 16,
						},
						"&:last-child": {
							borderBottomLeftRadius: 16,
							borderBottomRightRadius: 16,
						},
						borderRadius: 0,
					},
				},
			},
			MuiAlert: {
				styleOverrides: {
					root: {
						"& a": {
							color: palette.primary.main,
						},
					},
				},
			},
			MuiAppBar: {
				styleOverrides: {
					root: {
						backgroundColor: palette.app_bar.main,
						borderBottom: prefersDarkMode ? "1px solid #ffffff12" : "1px solid #ddd",
						// backdropFilter: `blur(10px)`
					},
				},
			},
			MuiAutocomplete: {
				styleOverrides: {
					paper: {
						backgroundColor: prefersDarkMode ? palette.primary.dark : "#ffffff",
					},
					root: {
						backgroundColor: "none",
						borderRadius: "10px",
					},
				},
			},
			MuiBackdrop: {
				styleOverrides: {
					root: {
						backgroundColor: prefersDarkMode && "#ffffff10",
						// backdropFilter: 'blur(10px)'
					},
				},
			},
			MuiBox: {
				styleOverrides: {
					input: {
						backgroundColor: prefersDarkMode ? palette.custom_chip.main : "#ffffff",
						border: prefersDarkMode ? `1px solid ${palette.company_light.main}` : "1px solid #ccc",
						color: prefersDarkMode ? "#ffffff" : "#000000",
					},
				},
			},
			MuiButton: {
				styleOverrides: {
					containedPrimary: {
						"&:hover": {
							backgroundColor: palette.button_color.main,
							color: "#FFFFFF",
						},
					},
					root: {
						borderRadius: 10,
					},
				},
			},
			MuiCardHeader: {
				styleOverrides: {
					root: {
						backgroundColor: palette.card_header.main,
						paddingBottom: 10,
						paddingTop: 10,
					},
				},
			},
			MuiCssBaseline: {
				styleOverrides: {
					html: {
						...darkScrollbar(
							prefersDarkMode
								? undefined
								: {
										active: grey[500],
										thumb: grey[400],
										track: grey[200],
									},
						),
						//scrollbarWidth for Firefox
						scrollbarWidth: "thin",
					},
				},
			},
			MuiDialog: {
				styleOverrides: {
					paper: {
						backgroundColor: prefersDarkMode ? palette.primary.dark : "#ffffff",
						backgroundImage: prefersDarkMode
							? "none"
							: "linear-gradient(rgba(255, 255, 255, 0.16), rgba(255, 255, 255, 0.16))",
						borderRadius: 16,
					},
				},
			},
			MuiList: {
				styleOverrides: {
					root: {
						"&.MuiMenu-list": {
							backgroundColor: prefersDarkMode ? palette.primary.dark : "#fff",
						},
					},
				},
			},
			MuiMenu: {
				defaultProps: {
					BackdropProps: {
						style: {
							backgroundColor: "transparent",
							// backdropFilter: 'blur(5px)'
						},
					},
				},
			},
			MuiOutlinedInput: {
				styleOverrides: {
					root: {
						backgroundColor: prefersDarkMode
							? palette.card_shine_through_primary.main
							: "#ffffff80",
						borderRadius: "10px",
					},
				},
			},
			MuiPaper: {
				styleOverrides: {
					root: {
						backgroundColor: palette.card_shine_through_primary.main,
						// backdropFilter: 'blur(10px)',
						border: 0,
						borderRadius: 16,
						overflow: "hidden",
					},
				},
			},
			MuiSelect: {
				styleOverrides: {
					root: {
						backgroundColor: prefersDarkMode
							? palette.card_shine_through_primary.main
							: "#ffffff80",
						borderRadius: "10px",
					},
				},
			},
			MuiTableHead: {
				styleOverrides: {
					root: {
						"& > tr > th:first-of-type": {
							borderTopLeftRadius: 9,
						},
						"& > tr > th:last-child": {
							borderTopRightRadius: 9,
						},
						backgroundColor: palette.card_header.main,
					},
				},
			},
			MuiTextField: {
				styleOverrides: {
					root: {
						"& .MuiOutlinedInput-root": {
							backgroundColor: prefersDarkMode
								? palette.card_shine_through_primary.main
								: "#ffffff80",
							borderRadius: "10px",
						},
					},
				},
			},
			MuiTypography: {
				styleOverrides: {
					root: {
						"& a": {
							color: palette.primary.main,
						},
					},
				},
			},
		},
		palette: { ...palette, mode: mode },
		typography: {
			button: {
				textTransform: "none",
			},
			fontFamily: ["Roboto", "Helvetica", "Arial", "sans-serif"].join(","),
		},
	});
};

const mapStateToProps = (state) => ({
	theme: state.shared_state.theme,
});

const mapDispatchToProps = (dispatch) => ({
	set_theme: (theme) => dispatch(set_theme(theme)),
});

export default connect(mapStateToProps, mapDispatchToProps)(TuxisTheme);
